import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  TimePicker,
  InputNumber,
  Row,
  Col,
  Radio,
  Select,
  Card,
  notification,
} from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import MapWithRoute from "../maps/MapWithRoute";
import AddressAutoComplete from "../maps/AddressAutoComplete";
import dayjs from "dayjs";
import { getActiveAirports } from "../../api/airport.service";

const JourneyForm = forwardRef((props, ref) => {
  const [journey, setJourneyType] = useState(props.initialValues?.type || "oneway");
  const [pickupType, setPickupType] = useState(props.initialValues?.pickupType || "airport");
  const [returnPickupType, setReturnPickupType] = useState(props.initialValues?.returnPickupType || "airport");
  const [destinationType, setDestinationType] = useState(props.initialValues?.destinationType || "address");
  const [returnDestinationType, setReturnDestinationType] = useState(props.initialValues?.returnDestinationType || "address");
  const [airports, setAirports] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedReturnDate, setSelectedReturnDate] = useState(null);

  const [form] = Form.useForm();

  // Watch fields for auto map update
  const pickup = Form.useWatch("pickup", form);
  const destination = Form.useWatch("destination", form);
  const additionalPickups = Form.useWatch("additionalPickups", form);
  const returnPickup = Form.useWatch("returnPickup", form);
  const returnDestination = Form.useWatch("returnDestination", form);
  const returnAdditionalPickups = Form.useWatch("returnAdditionalPickups", form);

  useImperativeHandle(ref, () => ({
    submit: () => form.submit(),
    validate: async () => {
      try {
        const values = await form.validateFields();
        return { valid: true, values };
      } catch (error) {
        return { valid: false, errors: error.errorFields };
      }
    },
    values: () => form.getFieldsValue(),
  }));

  // Fetch Airports
  useEffect(() => {
    const fetchAirports = async (retryCount = 0) => {
      try {
        const data = await getActiveAirports();
        const sorted = (data?.data || [])
          .filter((a) => a.status === 1 || a.status === undefined)
          .sort((a, b) => a.airportName.localeCompare(b.airportName))
          .map((elem) => ({
            value: elem.airportName,
            label: `${elem.airportName} | ${elem.airportCode}`,
          }));
        setAirports(sorted);
      } catch (err) {
        if (retryCount < 1) setTimeout(() => fetchAirports(retryCount + 1), 1000);
        else notification.error({ message: "Failed to fetch airports" });
      }
    };
    fetchAirports();
  }, []);

  // ========== AUTO MAP UPDATE (One Way) ==========
  useEffect(() => {
    if (pickup && destination && props.handleShowMap) {
      const timer = setTimeout(() => {
        props.handleShowMap();
      }, 500); // small debounce
      return () => clearTimeout(timer);
    }
  }, [pickup, destination, additionalPickups]);

  // ========== AUTO MAP UPDATE (Return) ==========
  useEffect(() => {
    if (journey === "return" && returnPickup && returnDestination && props.handleShowMapReturn) {
      const timer = setTimeout(() => {
        props.handleShowMapReturn();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [journey, returnPickup, returnDestination, returnAdditionalPickups]);

  // 6 Hour Buffer
  const disabledDate = (current) => {
    if (!current) return false;
    const bufferEnd = dayjs().add(6, "hour");
    return current.endOf("day").isBefore(bufferEnd);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    form.setFieldsValue({ time: null });
  };

  const handleReturnDateChange = (date) => {
    setSelectedReturnDate(date);
    form.setFieldsValue({ returnTime: null });
  };

  const disabledTime = () => {
    if (!selectedDate) {
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }
    const now = dayjs();
    const selected = selectedDate.startOf("day");
    const bufferEnd = now.add(6, "hours");
    const bufferHour = bufferEnd.hour();
    const bufferDay = bufferEnd.startOf("day");

    if (selected.isSame(now, "day")) {
      if (bufferEnd.isSame(now, "day")) {
        return {
          disabledHours: () =>
            Array.from({ length: 24 }, (_, i) => i).filter(
              (h) => h < now.hour() || h < bufferHour
            ),
          disabledMinutes: (selectedHour) => {
            if (selectedHour < now.hour() || selectedHour < bufferHour) {
              return Array.from({ length: 60 }, (_, i) => i);
            }
            return [];
          },
        };
      }
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }

    if (selected.format("YYYY-MM-DD") === bufferDay.format("YYYY-MM-DD")) {
      return {
        disabledHours: () => Array.from({ length: bufferHour + 1 }, (_, i) => i),
        disabledMinutes: (selectedHour) =>
          selectedHour < bufferHour ? Array.from({ length: 60 }, (_, i) => i) : [],
      };
    }
    return {};
  };

  const disabledReturnTime = () => {
    if (!selectedReturnDate) {
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }
    const now = dayjs();
    const selected = selectedReturnDate.startOf("day");
    const bufferEnd = now.add(6, "hours");
    const bufferHour = bufferEnd.hour();
    const bufferDay = bufferEnd.startOf("day");

    if (selected.isSame(now, "day")) {
      if (bufferEnd.isSame(now, "day")) {
        return {
          disabledHours: () =>
            Array.from({ length: 24 }, (_, i) => i).filter(
              (h) => h < now.hour() || h < bufferHour
            ),
          disabledMinutes: (selectedHour) => {
            if (selectedHour < now.hour() || selectedHour < bufferHour) {
              return Array.from({ length: 60 }, (_, i) => i);
            }
            return [];
          },
        };
      }
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }

    if (selected.format("YYYY-MM-DD") === bufferDay.format("YYYY-MM-DD")) {
      return {
        disabledHours: () => Array.from({ length: bufferHour + 1 }, (_, i) => i),
        disabledMinutes: (selectedHour) =>
          selectedHour < bufferHour ? Array.from({ length: 60 }, (_, i) => i) : [],
      };
    }
    return { disabledHours: () => [], disabledMinutes: () => [] };
  };

  const handleTypeChange = (e) => setJourneyType(e.target.value);

  const handlePickupTypeChange = (e) => {
    if (e.target.value === "address") {
      setDestinationType("airport");
      form.setFieldsValue({ destinationType: "airport", destination: undefined });
    }
    setPickupType(e.target.value);
    form.setFieldsValue({ pickup: undefined });
  };

  const handleReturnPickupTypeChange = (e) => {
    if (e.target.value === "address") {
      setReturnDestinationType("airport");
      form.setFieldsValue({ returnDestinationType: "airport", returnDestination: undefined });
    }
    setReturnPickupType(e.target.value);
    form.setFieldsValue({ returnPickup: undefined });
  };

  const handleDestinationTypeChange = (e) => {
    setDestinationType(e.target.value);
    form.setFieldsValue({ destination: undefined });
  };

  const handleReturnDestinationTypeChange = (e) => {
    setReturnDestinationType(e.target.value);
    form.setFieldsValue({ returnDestination: undefined });
  };

  // Common Card Style
  const cardStyle = {
    borderRadius: "16px",
    border: "2px solid #00401A",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0, 64, 26, 0.08)",
  };

  const cardHeadStyle = {
    background: "#00401A",
    borderBottom: "none",
    borderRadius: "14px 14px 0 0",
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={props.initialValues}
    >
      <Row gutter={[24, 24]}>
        {/* ================= LEFT SIDE ================= */}
        <Col xs={24} lg={11}>
          <div
            style={{
              border: "2px solid #00401A",
              borderRadius: "20px",
              padding: "16px",
              background: "#fff",
              boxShadow: "0 6px 20px rgba(0, 64, 26, 0.1)",
            }}
          >
            {/* User Details */}
            <Card
              title={
                <span className="text-white flex gap-2 items-center">
                  <UserOutlined /> User Details
                </span>
              }
              style={cardStyle}
              headStyle={cardHeadStyle}
              bodyStyle={{ padding: "20px" }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                    <Input placeholder="Enter your name" className="rounded-lg" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[{ required: true, type: "email" }]}
                  >
                    <Input placeholder="Enter your email" className="rounded-lg" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Phone"
                name="phone"
                rules={[
                  { required: true, message: "Phone number is required" },
                  {
                    pattern: /^\+[1-9]\d{1,14}$/,
                    message: "Please enter a valid phone number with country code",
                  },
                ]}
              >
                <Input placeholder="+923001234567" className="rounded-lg" />
              </Form.Item>

              <Form.Item name="type">
                <Radio.Group buttonStyle="solid" onChange={handleTypeChange}>
                  <Radio.Button value="oneway">One Way</Radio.Button>
                  <Radio.Button value="return">Return</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Card>

            {/* One Way Journey */}
            <Card
              className="mt-5"
              title={
                <span className="text-white flex gap-2 items-center">
                  <CalendarOutlined /> One Way Journey
                </span>
              }
              style={cardStyle}
              headStyle={cardHeadStyle}
              bodyStyle={{ padding: "20px" }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Date"
                    name="date"
                    rules={[{ required: true, message: "Please select a date" }]}
                  >
                    <DatePicker
                      className="w-full rounded-lg"
                      format="DD MMMM YYYY"
                      disabledDate={disabledDate}
                      onChange={handleDateChange}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Time"
                    name="time"
                    rules={[{ required: true, message: "Please select a time" }]}
                  >
                    <TimePicker
                      format="HH:mm"
                      className="w-full rounded-lg"
                      disabledTime={disabledTime}
                      showNow={false}
                      needConfirm={false}
                      minuteStep={5}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Pickup Type" name="pickupType" rules={[{ required: true }]}>
                    <Radio.Group onChange={handlePickupTypeChange} value={pickupType}>
                      <Radio value="airport">Airport</Radio>
                      <Radio value="address">Address</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Pickup"
                    name="pickup"
                    rules={[{ required: true, message: "Please enter pickup" }]}
                  >
                    {pickupType === "airport" ? (
                      <Select
                        showSearch
                        placeholder="Select Airport"
                        optionFilterProp="label"
                        options={airports}
                        className="rounded-lg"
                        allowClear
                      />
                    ) : (
                      <AddressAutoComplete
                        onPlaceSelected={(loc) => form.setFieldsValue({ pickup: loc.address })}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>

              {/* Additional Pickups */}
              <Form.List name="additionalPickups">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Row gutter={16} key={field.key} className="mb-2">
                        <Col span={10}>
                          <Form.Item
                            {...field}
                            name={[field.name, "type"]}
                            label={index === 0 ? "Add. Pickup Type" : ""}
                            initialValue="address"
                          >
                            <Radio.Group>
                              <Radio value="airport">Airport</Radio>
                              <Radio value="address">Address</Radio>
                            </Radio.Group>
                          </Form.Item>
                        </Col>
                        <Col span={10}>
                          <Form.Item
                            {...field}
                            name={[field.name, "pickup"]}
                            label={index === 0 ? "Additional Pickup" : ""}
                          >
                            <Input placeholder="Additional pickup location" className="rounded-lg" />
                          </Form.Item>
                        </Col>
                        <Col span={4} className="flex items-end">
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                            className="rounded-lg"
                          />
                        </Col>
                      </Row>
                    ))}
                    <Button
                      type="dashed"
                      block
                      icon={<PlusOutlined />}
                      onClick={() => add({ type: "address", pickup: "" })}
                      className="rounded-lg border-[#00401A] text-[#00401A]"
                    >
                      Add Additional Pickup
                    </Button>
                  </>
                )}
              </Form.List>

              <Row gutter={16} className="mt-4">
                <Col span={12}>
                  <Form.Item label="Destination Type" name="destinationType" rules={[{ required: true }]}>
                    <Radio.Group
                      onChange={handleDestinationTypeChange}
                      value={destinationType}
                      disabled={pickupType === "address"}
                    >
                      <Radio value="airport">Airport</Radio>
                      <Radio value="address">Address</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Destination"
                    name="destination"
                    rules={[{ required: true, message: "Please enter destination" }]}
                  >
                    {destinationType === "airport" ? (
                      <Select
                        showSearch
                        placeholder="Select Airport"
                        optionFilterProp="label"
                        options={airports}
                        className="rounded-lg"
                        allowClear
                      />
                    ) : (
                      <AddressAutoComplete
                        onPlaceSelected={(loc) => form.setFieldsValue({ destination: loc.address })}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16} className="mt-4">
                <Col span={12}>
                  <Form.Item label="Passenger Count" name="passengerCount" rules={[{ required: true }]}>
                    <InputNumber min={1} max={30} className="w-full rounded-lg" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Luggage Info (Optional)" name="luggageCount">
                    <Input placeholder="e.g. 2 bags" className="rounded-lg" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Flight Number (Optional)" name="flightNumber">
                <Input placeholder="Flight number" className="rounded-lg" />
              </Form.Item>
            </Card>

            {/* Return Journey */}
            {journey === "return" && (
              <Card
                className="mt-5"
                title={
                  <span className="text-white flex gap-2 items-center">
                    <CalendarOutlined /> Return Journey
                  </span>
                }
                style={cardStyle}
                headStyle={cardHeadStyle}
                bodyStyle={{ padding: "20px" }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Return Date" name="returnDate" rules={[{ required: true }]}>
                      <DatePicker
                        className="w-full rounded-lg"
                        format="DD MMMM YYYY"
                        disabledDate={disabledDate}
                        onChange={handleReturnDateChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Return Time" name="returnTime" rules={[{ required: true }]}>
                      <TimePicker
                        format="HH:mm"
                        className="w-full rounded-lg"
                        disabledTime={disabledReturnTime}
                        showNow={false}
                        needConfirm={false}
                        minuteStep={5}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Return Pickup Type" name="returnPickupType" rules={[{ required: true }]}>
                      <Radio.Group onChange={handleReturnPickupTypeChange} value={returnPickupType}>
                        <Radio value="airport">Airport</Radio>
                        <Radio value="address">Address</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Return Pickup" name="returnPickup" rules={[{ required: true }]}>
                      {returnPickupType === "airport" ? (
                        <Select
                          showSearch
                          placeholder="Select Airport"
                          optionFilterProp="label"
                          options={airports}
                          className="rounded-lg"
                          allowClear
                        />
                      ) : (
                        <AddressAutoComplete
                          onPlaceSelected={(loc) => form.setFieldsValue({ returnPickup: loc.address })}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Form.List name="returnAdditionalPickups">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field, index) => (
                        <Row gutter={16} key={field.key} className="mb-2">
                          <Col span={10}>
                            <Form.Item
                              {...field}
                              name={[field.name, "type"]}
                              label={index === 0 ? "Add. Pickup Type" : ""}
                              initialValue="address"
                            >
                              <Radio.Group>
                                <Radio value="airport">Airport</Radio>
                                <Radio value="address">Address</Radio>
                              </Radio.Group>
                            </Form.Item>
                          </Col>
                          <Col span={10}>
                            <Form.Item
                              {...field}
                              name={[field.name, "pickup"]}
                              label={index === 0 ? "Additional Pickup" : ""}
                            >
                              <Input placeholder="Additional pickup" className="rounded-lg" />
                            </Form.Item>
                          </Col>
                          <Col span={4} className="flex items-end">
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(field.name)}
                              className="rounded-lg"
                            />
                          </Col>
                        </Row>
                      ))}
                      <Button
                        type="dashed"
                        block
                        icon={<PlusOutlined />}
                        onClick={() => add({ type: "address", pickup: "" })}
                        className="rounded-lg border-[#00401A] text-[#00401A]"
                      >
                        Add Additional Pickup
                      </Button>
                    </>
                  )}
                </Form.List>

                <Row gutter={16} className="mt-4">
                  <Col span={12}>
                    <Form.Item label="Return Destination Type" name="returnDestinationType" rules={[{ required: true }]}>
                      <Radio.Group
                        onChange={handleReturnDestinationTypeChange}
                        value={returnDestinationType}
                        disabled={returnPickupType === "address"}
                      >
                        <Radio value="airport">Airport</Radio>
                        <Radio value="address">Address</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Return Destination" name="returnDestination" rules={[{ required: true }]}>
                      {returnDestinationType === "airport" ? (
                        <Select
                          showSearch
                          placeholder="Select Airport"
                          optionFilterProp="label"
                          options={airports}
                          className="rounded-lg"
                          allowClear
                        />
                      ) : (
                        <AddressAutoComplete
                          onPlaceSelected={(loc) =>
                            form.setFieldsValue({ returnDestination: loc.address })
                          }
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16} className="mt-4">
                  <Col span={12}>
                    <Form.Item label="Passenger Count" name="returnPassengerCount" rules={[{ required: true }]}>
                      <InputNumber min={1} max={30} className="w-full rounded-lg" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Luggage Info (Optional)" name="returnLuggageCount">
                      <Input placeholder="e.g. 2 bags" className="rounded-lg" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item label="Flight Number (Optional)" name="returnFlightNumber">
                  <Input className="rounded-lg" />
                </Form.Item>
              </Card>
            )}
          </div>
        </Col>

        {/* ================= RIGHT SIDE (MAP) ================= */}
        <Col xs={24} lg={13}>
          <div
            style={{
              border: "2px solid #00401A",
              borderRadius: "20px",
              padding: "16px",
              background: "#fff",
              boxShadow: "0 6px 20px rgba(0, 64, 26, 0.1)",
              height: "100%",
            }}
          >
            <Card
              title={
                <span className="text-white flex gap-2 items-center">
                  <EnvironmentOutlined /> Live Route Map
                </span>
              }
              style={{ ...cardStyle, height: "100%" }}
              headStyle={cardHeadStyle}
              bodyStyle={{ padding: "16px" }}
            >
              <div
                style={{ height: "580px" }}
                className="rounded-2xl overflow-hidden mb-4 border border-gray-100"
              >
                {props.directions ? (
                  <MapWithRoute height="580px" directions={props.directions} data={props.data} />
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400 bg-gray-50 text-center px-4 rounded-2xl">
                    Select pickup & destination — route will appear automatically
                  </div>
                )}
              </div>

              <div className="bg-[#E8F5E9] rounded-2xl p-4 grid grid-cols-2 text-center mb-4 border border-[#00401A]/20">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Distance</p>
                  <b className="text-[#00401A] text-lg">{props.data?.totalDistance || "0 Km"}</b>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">ETA</p>
                  <b className="text-[#00401A] text-lg">{props.data?.totalDuration || "0 mins"}</b>
                </div>
              </div>

              {journey === "return" && (
                <>
                  <div
                    style={{ height: "580px" }}
                    className="rounded-2xl overflow-hidden mb-4 border border-gray-100"
                  >
                    {props.directionsReturn ? (
                      <MapWithRoute
                        height="580px"
                        directions={props.directionsReturn}
                        data={props.dataReturn}
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center text-gray-400 bg-gray-50 text-center px-4 rounded-2xl">
                        Select return pickup & destination
                      </div>
                    )}
                  </div>

                  <div className="bg-[#E8F5E9] rounded-2xl p-4 grid grid-cols-2 text-center border border-[#00401A]/20">
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Return Distance</p>
                      <b className="text-[#00401A] text-lg">
                        {props.dataReturn?.totalDistance || "0 Km"}
                      </b>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Return ETA</p>
                      <b className="text-[#00401A] text-lg">
                        {props.dataReturn?.totalDuration || "0 mins"}
                      </b>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </div>
        </Col>
      </Row>
    </Form>
  );
});

export default JourneyForm;